import React from "react";
import { AppHeader } from "./CommunitySelection";
import { VISION_IMG } from "../lib/constants";
import { useLang } from "../lib/i18n";
import { RotateCcw, MessagesSquare, Users2, ArrowRight, AlertTriangle, Landmark, HeartHandshake, Megaphone } from "lucide-react";

const ROOT_CAUSE_PROMPTS = [
  { t: "The policy barrier", q: "Which external barrier — the benefits cliff, strict work-reporting rules, or predatory lending rates — made it feel like working harder wasn't enough?" },
  { t: "The turning point", q: "At what specific moment did you feel that every remaining option would lead to a crisis?" },
  { t: "The root cause", q: "Which underlying structural issue was the real driver of this outcome, rather than personal choice or bad luck?" },
];

const DEFAULT_INSIGHTS = [
  { label: "The Benefits Cliff", type: "Policy rule", insight: "A wage increase of about $1.50/hour can trigger an immediate loss of $600+ a month in childcare or healthcare subsidies — earning more can leave a family worse off." },
  { label: "Predatory Financing", type: "Market trap", insight: "Lack of access to standard banking funnels families into payday and title loans with APRs exceeding 300%." },
  { label: "The Cost of Being Poor", type: "Market trap", insight: "Late fees, utility reconnection charges, and transportation gaps create compounding financial penalties." },
];

const ROOT_CAUSES = [
  { t: "Housing instability & cost burden", d: "When rent takes most of every dollar, there's nothing left to absorb a shock." },
  { t: "Unreliable transportation", d: "No car and thin transit can cost a job, a clinic visit, or a child's school day." },
  { t: "Limited access to healthcare", d: "Delayed or rationed care turns manageable problems into emergencies." },
  { t: "Lack of affordable childcare", d: "Without care, working and parenting collide into impossible choices." },
  { t: "Unstable or low-wage work", d: "Fluctuating hours and daily pay make it nearly impossible to plan or save." },
  { t: "The digital divide", d: "No home internet becomes a barrier to school, work, and opportunity." },
  { t: "The high cost of being poor", d: "Late fees, penalties, and short-term borrowing deepen the hole." },
];

const NEIGHBORS = [
  "Returning citizens rebuilding their lives after incarceration",
  "Families living with generational poverty",
  "Grandparents raising grandchildren",
  "Young people aging out of foster care",
];

const QUESTIONS = [
  "Which barrier surprised you most — and why?",
  "Where did you see one problem cascade into another?",
  "What did this neighbor have to go without?",
  "How might these same barriers show up in our own community?",
  "What would it take for us to address a root cause, not only the emergency?",
  "How can we advocate for full participation, not just provide relief?",
];

export const DebriefScreen = ({ onRestart, onContribute, summary }) => {
  const { t } = useLang();
  const profile = summary?.ending_profile;
  const insights = summary?.systemic_barriers?.length ? summary.systemic_barriers : DEFAULT_INSIGHTS;
  const topBarriers = (summary?.barriers || []).slice(0, 3);
  return (
    <div className="min-h-screen">
      <AppHeader subtitle={t("Your report")} />
      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-10 sm:py-12">
        {/* 1 — Outcome & impact */}
        {summary && (
          <div className="animate-rise bg-white rounded-lg border border-[#8E2929]/25 overflow-hidden">
            <div className="bg-[#8E2929]/8 px-6 sm:px-7 py-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-[#8E2929]" strokeWidth={2} />
              <span className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#8E2929]">{t("Outcome & impact")}</span>
            </div>
            <div className="p-6 sm:p-7">
              {profile && (
                <h2 className="font-display text-2xl sm:text-3xl font-semibold leading-tight" style={{ color: profile.color }}>
                  {profile.headline}
                </h2>
              )}
              <div className="mt-4 flex flex-wrap gap-2">
                {summary.borrowed > 0 && (
                  <span className="text-xs font-semibold text-[#8E2929] bg-[#8E2929]/8 px-3 py-1.5 rounded-full">
                    {t("Debt accumulated:")} ${summary.borrowed.toLocaleString()} {t("borrowed ·")} ${summary.monthly_debt}{t("/mo")} {t("owed")}
                  </span>
                )}
                {topBarriers.map((b) => (
                  <span key={b.key} className="text-xs font-semibold text-vin bg-vin/8 px-3 py-1.5 rounded-full">{b.label}</span>
                ))}
              </div>
              <p className="mt-5 text-[15px] text-ink leading-relaxed">
                {t("You made the most reasonable choices available to you, yet the situation still unraveled. In chronic scarcity, individual effort alone cannot overcome compounding structural hurdles.")}
              </p>
            </div>
          </div>
        )}

        {/* 2 — Identifying the root causes */}
        <div className="mt-6 bg-white rounded-lg border border-[var(--line)] p-6 sm:p-7">
          <div className="flex items-center gap-2 text-vin mb-4">
            <MessagesSquare className="w-4 h-4" strokeWidth={1.75} />
            <span className="text-[11px] uppercase tracking-[0.14em] font-semibold">{t("Identifying the root causes")}</span>
          </div>
          <div className="space-y-4">
            {ROOT_CAUSE_PROMPTS.map((p) => (
              <div key={p.t}>
                <div className="text-[11px] uppercase tracking-[0.12em] font-semibold text-vin/70">{t(p.t)}</div>
                <p className="mt-1 text-[15px] text-ink leading-relaxed">{t(p.q)}</p>
              </div>
            ))}
          </div>
        </div>

        {/* 3 — Systemic insights */}
        <div className="mt-6">
          <h3 className="font-display text-xl font-semibold text-ink mb-1">{t("The forces behind what you felt")}</h3>
          <p className="text-sm text-ink-soft mb-4">{t("These are the external, systemic mechanics behind the choices — not personal failings.")}</p>
          <div className="grid grid-cols-1 gap-3">
            {insights.map((s) => (
              <div key={s.key || s.label} data-testid="systemic-insight" className="bg-white rounded-lg border border-[var(--line)] p-5">
                <div className="flex items-center gap-2">
                  <span className={`text-[10px] uppercase tracking-[0.1em] font-bold px-2 py-0.5 rounded-full ${s.type === "Policy rule" ? "text-[#5A2A85] bg-[#5A2A85]/10" : "text-[#8E5A00] bg-[#8E5A00]/10"}`}>
                    {t(s.type)}
                  </span>
                  <span className="font-semibold text-ink text-[15px]">{t(s.label)}</span>
                </div>
                <p className="text-sm text-ink-soft mt-2 leading-relaxed">{t(s.insight)}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 mb-2 animate-rise">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-vin/70 mb-3">
            {t("The whole person")}
          </p>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-ink leading-tight">
            {t("Now, let's talk about root causes")}
          </h2>
          <p className="mt-4 text-base text-ink-soft leading-relaxed">
            {t("This experience was never about one family's choices. It was about the conditions and barriers that shape those choices — the root causes of poverty. When we can see them clearly, we can begin to serve the whole person and advocate for their full participation in community life.")}
          </p>
        </div>

        {/* Neighbors we serve */}
        <div className="mt-8 bg-white rounded-lg border border-[var(--line)] p-6 sm:p-7">
          <div className="flex items-center gap-2 text-vin mb-4">
            <Users2 className="w-4 h-4" strokeWidth={1.75} />
            <span className="text-[11px] uppercase tracking-[0.14em] font-semibold">{t("Who this represents")}</span>
          </div>
          <p className="text-sm text-ink-soft leading-relaxed mb-4">
            {t("The realities in this experience are lived every day by many people — among them:")}
          </p>
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
            {NEIGHBORS.map((n) => (
              <li key={n} className="text-sm text-ink flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-vin shrink-0" />
                {t(n)}
              </li>
            ))}
          </ul>
        </div>

        {/* Root causes named */}
        <div className="mt-6">
          <h3 className="font-display text-xl font-semibold text-ink mb-4">{t("Naming the barriers")}</h3>
          <div className="grid grid-cols-1 gap-3">
            {ROOT_CAUSES.map((r) => (
              <div key={r.t} className="bg-white rounded-lg border border-[var(--line)] p-5">
                <div className="font-semibold text-ink text-[15px]">{t(r.t)}</div>
                <div className="text-sm text-ink-soft mt-1 leading-relaxed">{t(r.d)}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Principles of Human Dignity & the Common Good */}
        <div className="mt-8 relative overflow-hidden rounded-lg bg-vin-dark">
          <img src={VISION_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" aria-hidden="true" />
          <div className="absolute inset-0 bg-vin-dark/75" aria-hidden="true" />
          <div className="relative z-10 p-7 sm:p-9">
            <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-white/60 mb-4">
              {t("Principles of Human Dignity & the Common Good")}
            </p>
            <p className="text-lg text-white leading-relaxed font-body">
              {t("These principles call us to honor the dignity of every person and to work for the common good. Until we can fully see the barriers our neighbors face, we cannot serve the whole person — or advocate for the policies that promote their full participation. Before we offer advice, we are called first to understand, and to listen.")}
            </p>
          </div>
        </div>

        {/* Discussion questions */}
        <div className="mt-8 bg-white rounded-lg border border-[var(--line)] p-6 sm:p-7">
          <div className="flex items-center gap-2 text-vin mb-4">
            <MessagesSquare className="w-4 h-4" strokeWidth={1.75} />
            <span className="text-[11px] uppercase tracking-[0.14em] font-semibold">{t("To discuss together")}</span>
          </div>
          <ol className="space-y-3">
            {QUESTIONS.map((q, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-ink leading-relaxed">
                <span className="mt-0.5 h-6 w-6 rounded-full bg-vin/10 text-vin flex items-center justify-center text-xs font-semibold shrink-0">
                  {i + 1}
                </span>
                {t(q)}
              </li>
            ))}
          </ol>
        </div>

        {/* 4 — From empathy to action */}
        <div className="mt-8 bg-vin text-white rounded-lg p-6 sm:p-7">
          <div className="flex items-center gap-2 text-white/80 mb-4">
            <HeartHandshake className="w-4 h-4" strokeWidth={1.75} />
            <span className="text-[11px] uppercase tracking-[0.14em] font-semibold">{t("From empathy to action")}</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="flex gap-3">
              <Users2 className="w-5 h-5 shrink-0 mt-0.5 text-white/80" strokeWidth={1.75} />
              <div>
                <div className="font-semibold text-[15px]">{t("In your community")}</div>
                <p className="text-sm text-white/85 mt-1 leading-relaxed">{t("Listen deeply for these hidden systemic pressures when discerning how to walk alongside a neighbor.")}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Megaphone className="w-5 h-5 shrink-0 mt-0.5 text-white/80" strokeWidth={1.75} />
              <div>
                <div className="font-semibold text-[15px]">{t("Beyond direct aid")}</div>
                <p className="text-sm text-white/85 mt-1 leading-relaxed">{t("Consider how your organization or group can engage in systemic change — local advocacy and community education to help fix these broken rules.")}</p>
              </div>
            </div>
          </div>
          <a
            href="/voices"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="debrief-voices-link"
            className="mt-6 inline-flex items-center gap-2 bg-white text-vin font-semibold px-6 py-3 rounded-full hover:bg-white/90 transition-colors duration-200"
          >
            {t("Explore Policy Pathways")}
            <ArrowRight className="w-4 h-4" strokeWidth={1.75} />
          </a>
        </div>

        {/* CTAs */}
        <div className="mt-10 pt-8 border-t border-[var(--line)]">
          <div className="bg-white rounded-lg border border-vin/20 p-6 sm:p-7 text-center">
            <h3 className="font-display text-xl font-semibold text-ink">{t("Now, look at your own community")}</h3>
            <p className="mt-2 text-sm text-ink-soft leading-relaxed max-w-lg mx-auto">
              {t("What barriers do families face where you serve? Share what you know and the public data you've seen — your contribution helps us understand root causes close to home.")}
            </p>
            <button
              data-testid="debrief-contribute-button"
              onClick={onContribute}
              className="mt-5 inline-flex items-center gap-2 bg-vin text-white font-semibold px-7 py-3.5 rounded-full hover:bg-vin-hover transition-colors duration-200"
            >
              {t("Contribute your community")}
              <ArrowRight className="w-5 h-5" strokeWidth={1.75} />
            </button>
          </div>
          <div className="mt-6 text-center">
            <a
              href="/lessons"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="debrief-lessons-link"
              className="inline-flex items-center gap-2 text-vin font-semibold px-6 py-3 rounded-full hover:bg-vin/5 transition-colors duration-200"
            >
              <MessagesSquare className="w-4 h-4" strokeWidth={1.75} />
              {t("Read & share on the Lessons Learned wall")}
            </a>
          </div>
          <div className="mt-2 text-center">
            <button
              data-testid="restart-button"
              onClick={onRestart}
              className="inline-flex items-center gap-2 text-vin font-semibold px-6 py-3 rounded-full hover:bg-vin/5 transition-colors duration-200"
            >
              <RotateCcw className="w-4 h-4" strokeWidth={1.75} />
              {t("Walk with another neighbor")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DebriefScreen;
